// Nuevo componente PlaceListMobile.tsx
import React from 'react';
import { Box, List, ListItem, ListItemText, Typography, Divider, CircularProgress } from '@mui/material';
import { Place, PlaceType } from '../Place';
import { useUserLocation } from './useUserLocation';

interface PlaceListMobileProps {
  places: Place[];
  hasFilters: boolean;
  onPlaceClick: (place: Place) => void;
}

const PlaceListMobile: React.FC<PlaceListMobileProps> = ({ places, hasFilters, onPlaceClick }) => {
  const [loading, setLoading] = React.useState(false);
  const [visiblePlaces, setVisiblePlaces] = React.useState<Place[]>([]);
  const [page, setPage] = React.useState(1);
  const itemsPerPage = 10;
  const userLocation = useUserLocation();
  const listRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setVisiblePlaces(places.slice(0, itemsPerPage));
    setPage(1);
    
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [places]);

  const handleScroll = () => {
    if (!listRef.current || loading) return;
    
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const isNearBottom = scrollHeight - (scrollTop + clientHeight) < 50;
    
    if (isNearBottom && visiblePlaces.length < places.length) {
      setLoading(true);
      setTimeout(() => {
        const nextPage = page + 1;
        setVisiblePlaces([...visiblePlaces, ...places.slice(page * itemsPerPage, nextPage * itemsPerPage)]);
        setPage(nextPage);
        setLoading(false);
      }, 300);
    }
  };

  const calculateDistance = (lat: number, lng: number) => {
    if (!userLocation) return null;
    
    const R = 6371; // Radio de la Tierra en km
    const dLat = (lat - userLocation.lat) * Math.PI / 180;
    const dLng = (lng - userLocation.lng) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(userLocation.lat * Math.PI / 180) * Math.cos(lat * Math.PI / 180) * 
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return (R * c).toFixed(1); // Distancia en km con 1 decimal
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <Typography variant="h6" sx={{ 
        p: 2, 
        borderBottom: '1px solid #eee',
        position: 'sticky',
        top: 0,
        backgroundColor: 'white',
        zIndex: 1
      }}>
        Lugares encontrados ({places.length})
      </Typography>
      
      <Box 
        ref={listRef}
        onScroll={handleScroll}
        sx={{ 
          flex: 1,
          overflowY: 'auto',
          paddingBottom: '8px',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888',
            borderRadius: '2px',
          },
        }}
      >
        <List>
          {visiblePlaces.map((place, index) => (
            <React.Fragment key={place.id}>
              <ListItem 
                 
                onClick={() => onPlaceClick(place)}
                sx={{
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {place.name}
                      </Typography>
                      {userLocation && (
                        <Typography variant="body2" color="primary">
                          {calculateDistance(place.lat, place.longi)} km
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {place.placeType }
                    </Typography>
                  }
                />
              </ListItem>
              {index < visiblePlaces.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </List>
      </Box>
    </Box>
  );
};

export default PlaceListMobile;